import { useState, useMemo, useRef, useCallback } from 'react';
import { Sidebar, useSidebar } from '@/components/Sidebar';
import {
  ChevronLeft,
  ChevronRight,
  Plus,
  ArrowUpCircle,
  ArrowDownCircle,
  Calendar as CalendarIcon,
  TrendingUp,
  TrendingDown,
  List,
  Settings,
  Wallet,
  CalendarClock,
} from 'lucide-react';

import { PageHeader } from '@/components/PageHeader';
import { RecurringTransactionModal } from '@/components/RecurringTransactionModal';
import { ManageRecurrencesModal } from '@/components/ManageRecurrencesModal';
import { ModifyRecurrenceDialog } from '@/components/ModifyRecurrenceDialog';
import {
  useRecurringTransactions,
  type RecurringTransaction,
} from '@/hooks/useRecurringTransactions';
import { useTranslation } from '@/hooks/useTranslation';
import { useConfirm } from '@/components/ui/confirm-dialog';
import { useCurrency } from '@/contexts/CurrencyContext';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { getSubscriptionLogo } from '@/lib/brands';
import { useExplorationAchievement } from '@/hooks/useAchievementTriggers';

type ViewMode = 'calendar' | 'list';

export default function CalendarPage() {
  const { collapsed: sidebarCollapsed } = useSidebar();
  useExplorationAchievement('explore_calendar');
  const { t, language } = useTranslation();
  const confirm = useConfirm();
  const { formatAmount } = useCurrency();
  const { user } = useAuth();
  const {
    recurringTransactions,
    activeRecurring,
    createRecurringTransaction,
    updateRecurringTransaction,
    deleteRecurringTransaction,
    loadRecurringTransactions,
    toggleActive,
  } = useRecurringTransactions();

  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState<ViewMode>('calendar');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isManageOpen, setIsManageOpen] = useState(false);
  const [selectedDay, setSelectedDay] = useState<number | null>(null);
  const [expandedDay, setExpandedDay] = useState<number | null>(null);
  const [editingRecurring, setEditingRecurring] = useState<RecurringTransaction | null>(null);
  const [pendingEditData, setPendingEditData] = useState<any>(null);
  const [isModifyDialogOpen, setIsModifyDialogOpen] = useState(false);
  const pendingModifyRef = useRef(false);
  const detailPanelRef = useRef<HTMLDivElement>(null);

  // Handle save (create or update)
  const handleSaveRecurring = async (data: any) => {
    if (editingRecurring) {
      // Store pending data and show modify dialog
      setPendingEditData(data);
      setIsModifyDialogOpen(true);
      pendingModifyRef.current = true;
      return { success: true }; // Close the edit modal
    }
    return createRecurringTransaction(data);
  };

  // Handle modify option selection
  const handleModifyConfirm = async (option: 'include_current' | 'from_next') => {
    if (!editingRecurring || !pendingEditData || !user) return;

    // Update the recurring transaction with new values
    await updateRecurringTransaction(editingRecurring.id, {
      description: pendingEditData.description,
      amount: pendingEditData.amount,
      type: pendingEditData.type,
      frequency: pendingEditData.frequency,
      category_id: pendingEditData.category_id,
      account_id: pendingEditData.account_id,
      end_date: pendingEditData.end_date || null,
    });

    // Option 1: Also update the current month's transaction
    if (option === 'include_current') {
      const now = new Date();
      const monthStart = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
      const monthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0)
        .toISOString()
        .split('T')[0];

      // Find the transaction for the current month matching this recurring transaction
      const { data: matchingTxns } = await supabase
        .from('transactions')
        .select('id')
        .eq('user_id', user.id)
        .eq('account_id', editingRecurring.account_id ?? '')
        .eq('description', editingRecurring.description)
        .eq('type', editingRecurring.type)
        .gte('date', monthStart)
        .lte('date', monthEnd)
        .limit(1);

      if (matchingTxns && matchingTxns.length > 0) {
        await supabase
          .from('transactions')
          .update({
            description: pendingEditData.description,
            amount: pendingEditData.amount,
            type: pendingEditData.type,
            category_id: pendingEditData.category_id || null,
            account_id: pendingEditData.account_id,
            updated_at: new Date().toISOString(),
          })
          .eq('id', matchingTxns[0].id)
          .eq('user_id', user.id);
      }
    }
    // Option 2 (from_next): We already updated the recurring, nothing else needed

    setIsModifyDialogOpen(false);
    setPendingEditData(null);
    setEditingRecurring(null);
    await loadRecurringTransactions();
  };

  // Handle day selection with smooth scroll
  const handleSelectDay = useCallback((day: number | null) => {
    setSelectedDay(day);
    if (day !== null) {
      // Wait for the panel to render, then scroll with offset
      setTimeout(() => {
        if (detailPanelRef.current) {
          const elementPosition = detailPanelRef.current.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.scrollY - 120; // 120px offset for header
          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth',
          });
        }
      }, 100);
    }
  }, []);

  // Current month info
  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const firstDayOfWeek = (new Date(year, month, 1).getDay() + 6) % 7; // Monday = 0

  const monthName = currentDate.toLocaleString('default', { month: 'long', year: 'numeric' });

  // Get all occurrences in the current month
  const monthOccurrences = useMemo(() => {
    const startOfMonth = new Date(year, month, 1);
    const endOfMonth = new Date(year, month + 1, 0, 23, 59, 59);
    const occurrences: Array<{ recurring: (typeof activeRecurring)[0]; date: Date }> = [];

    activeRecurring.forEach((r) => {
      let nextDate = parseLocalDate(r.next_occurrence);
      const rEndDate = r.end_date ? parseLocalDate(r.end_date) : null;

      // Rewind to find the first occurrence that could be in this month
      // Go back to start_date and iterate forward
      const startDate = parseLocalDate(r.start_date);
      nextDate = new Date(startDate);

      // Fast-forward to near the month
      while (nextDate < startOfMonth) {
        const next = advanceDate(nextDate, r.frequency);
        if (next <= nextDate) break; // Safety against infinite loop
        nextDate = next;
      }

      // Collect all occurrences in this month
      while (nextDate <= endOfMonth) {
        if (rEndDate && nextDate > rEndDate) break;
        if (nextDate >= startOfMonth && nextDate <= endOfMonth) {
          occurrences.push({ recurring: r, date: new Date(nextDate) });
        }
        const next = advanceDate(nextDate, r.frequency);
        if (next <= nextDate) break;
        nextDate = next;
      }
    });

    return occurrences;
  }, [activeRecurring, year, month]);

  // Group by day
  const occurrencesByDay = useMemo(() => {
    const map = new Map<number, typeof monthOccurrences>();
    monthOccurrences.forEach((occ) => {
      const day = occ.date.getDate();
      if (!map.has(day)) map.set(day, []);
      map.get(day)!.push(occ);
    });
    return map;
  }, [monthOccurrences]);

  // Summary stats
  const summary = useMemo(() => {
    let income = 0;
    let expenses = 0;
    monthOccurrences.forEach((occ) => {
      if (occ.recurring.type === 'income') income += occ.recurring.amount;
      else expenses += occ.recurring.amount;
    });
    return { income, expenses, net: income - expenses, total: monthOccurrences.length };
  }, [monthOccurrences]);

  // Next 7 days: compute total amount of occurrences in the coming 7 days
  const next7DaysAmount = useMemo(() => {
    const now = new Date();
    const in7Days = new Date(now);
    in7Days.setDate(in7Days.getDate() + 7);
    let total = 0;
    let count = 0;

    activeRecurring.forEach((r) => {
      const startDate = parseLocalDate(r.start_date);
      const rEndDate = r.end_date ? parseLocalDate(r.end_date) : null;
      let nextDate = new Date(startDate);

      // Fast-forward to today
      while (nextDate < now) {
        const next = advanceDate(nextDate, r.frequency);
        if (next <= nextDate) break;
        nextDate = next;
      }

      // Collect occurrences in the next 7 days
      while (nextDate <= in7Days) {
        if (rEndDate && nextDate > rEndDate) break;
        if (nextDate >= now && nextDate <= in7Days) {
          total += r.amount;
          count++;
        }
        const next = advanceDate(nextDate, r.frequency);
        if (next <= nextDate) break;
        nextDate = next;
      }
    });

    return { total, count };
  }, [activeRecurring]);

  // Sorted list of all occurrences
  const sortedOccurrences = useMemo(
    () => [...monthOccurrences].sort((a, b) => a.date.getTime() - b.date.getTime()),
    [monthOccurrences]
  );

  // Navigation
  const goToPrevMonth = () => setCurrentDate(new Date(year, month - 1, 1));
  const goToNextMonth = () => setCurrentDate(new Date(year, month + 1, 1));
  const goToToday = () => setCurrentDate(new Date());

  const today = new Date();
  const isCurrentMonth = today.getFullYear() === year && today.getMonth() === month;

  // Build calendar grid
  const calendarDays = useMemo(() => {
    const days: Array<{ day: number; isCurrentMonth: boolean }> = [];

    // Prev month padding
    const prevMonthDays = new Date(year, month, 0).getDate();
    for (let i = firstDayOfWeek - 1; i >= 0; i--) {
      days.push({ day: prevMonthDays - i, isCurrentMonth: false });
    }

    // Current month
    for (let d = 1; d <= daysInMonth; d++) {
      days.push({ day: d, isCurrentMonth: true });
    }

    // Next month padding
    const remaining = 42 - days.length; // 6 rows
    for (let i = 1; i <= remaining; i++) {
      days.push({ day: i, isCurrentMonth: false });
    }

    return days;
  }, [year, month, daysInMonth, firstDayOfWeek]);

  const weekDays = [
    t('calendar.mon' as any),
    t('calendar.tue' as any),
    t('calendar.wed' as any),
    t('calendar.thu' as any),
    t('calendar.fri' as any),
    t('calendar.sat' as any),
    t('calendar.sun' as any),
  ];

  return (
    <div className="min-h-screen bg-[#f9fafb] dark:bg-[#0f1117]">
      <Sidebar activePage="calendar" />
      <main className={`${sidebarCollapsed ? 'lg:ml-[72px]' : 'lg:ml-64'}`}>
        <PageHeader
          title={t('calendar.title' as any)}
          subtitle={t('calendar.subtitle' as any)}
          action={
            <div className="flex items-center gap-3">
              {/* Manage recurrences */}
              <button
                id="guide-calendar"
                onClick={() => setIsManageOpen(true)}
                className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-xl text-sm font-medium transition-all hover:bg-gray-50 dark:hover:bg-gray-700 scroll-mt-24 target:ring-4 target:ring-[#16c47f]/30 target:ring-offset-4"
              >
                <Settings className="w-4 h-4" />
                {t('calendar.manageRecurrences' as any)}
              </button>
            </div>
          }
        />

        <div className="p-6 space-y-6">
          {/* Today's date */}
          <p className="text-xs text-gray-400 dark:text-gray-500">
            {new Date().toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US', {
              weekday: 'long',
              day: 'numeric',
              month: 'long',
              year: 'numeric',
            })}
          </p>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            {/* Fixed Expenses */}
            <div className="bg-white dark:bg-gray-800 rounded-2xl p-5 border border-gray-100 dark:border-gray-700">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-red-50 dark:bg-red-900/20 flex items-center justify-center flex-shrink-0">
                  <TrendingDown className="w-5 h-5 text-red-500" />
                </div>
                <div className="min-w-0">
                  <span className="text-[10px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-widest">
                    {t('calendar.fixedExpenses' as any)}
                  </span>
                  <p className="text-xl font-bold text-gray-900 dark:text-white leading-tight">
                    {formatAmount(summary.expenses)}
                  </p>
                </div>
              </div>
            </div>

            {/* Recurring Income */}
            <div className="bg-white dark:bg-gray-800 rounded-2xl p-5 border border-gray-100 dark:border-gray-700">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-emerald-50 dark:bg-emerald-900/20 flex items-center justify-center flex-shrink-0">
                  <TrendingUp className="w-5 h-5 text-emerald-500" />
                </div>
                <div className="min-w-0">
                  <span className="text-[10px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-widest">
                    {t('calendar.recurringIncome' as any)}
                  </span>
                  <p className="text-xl font-bold text-gray-900 dark:text-white leading-tight">
                    {formatAmount(summary.income)}
                  </p>
                </div>
              </div>
            </div>

            {/* Forecast Balance */}
            <div className="bg-white dark:bg-gray-800 rounded-2xl p-5 border border-gray-100 dark:border-gray-700">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-emerald-50 dark:bg-emerald-900/20 flex items-center justify-center flex-shrink-0">
                  <Wallet className="w-5 h-5 text-emerald-500" />
                </div>
                <div className="min-w-0">
                  <span className="text-[10px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-widest">
                    {t('calendar.forecastBalance' as any)}
                  </span>
                  <p className="text-xl font-bold text-gray-900 dark:text-white leading-tight">
                    {formatAmount(summary.net)}
                  </p>
                </div>
              </div>
            </div>

            {/* Next 7 Days */}
            <div className="bg-white dark:bg-gray-800 rounded-2xl p-5 border border-gray-100 dark:border-gray-700">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-indigo-50 dark:bg-indigo-900/20 flex items-center justify-center flex-shrink-0">
                  <CalendarClock className="w-5 h-5 text-indigo-500" />
                </div>
                <div className="min-w-0">
                  <span className="text-[10px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-widest">
                    {t('calendar.next7Days' as any)}
                  </span>
                  <p className="text-xl font-bold text-gray-900 dark:text-white leading-tight">
                    {formatAmount(next7DaysAmount.total)}
                  </p>
                  <span className="text-[10px] text-gray-400 dark:text-gray-500">
                    {t('calendar.upcomingDue' as any)}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Month Navigation */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <button
                onClick={goToPrevMonth}
                className="p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              >
                <ChevronLeft className="w-5 h-5 text-gray-600 dark:text-gray-400" />
              </button>
              <h2 className="text-lg font-bold text-gray-900 dark:text-white capitalize min-w-[200px] text-center">
                {monthName}
              </h2>
              <button
                onClick={goToNextMonth}
                className="p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              >
                <ChevronRight className="w-5 h-5 text-gray-600 dark:text-gray-400" />
              </button>
              {!isCurrentMonth && (
                <button
                  onClick={goToToday}
                  className="ml-2 px-3 py-1.5 text-xs font-medium bg-[#16c47f]/10 text-[#16c47f] rounded-lg hover:bg-[#16c47f]/20 transition-colors"
                >
                  {t('calendar.today' as any)}
                </button>
              )}
            </div>
            <div className="flex items-center gap-3">
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {activeRecurring.length} {t('calendar.activeSubscriptions' as any)}
              </p>
              <div className="flex bg-gray-100 dark:bg-gray-800 rounded-xl p-1">
                <button
                  onClick={() => setViewMode('calendar')}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                    viewMode === 'calendar'
                      ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                      : 'text-gray-500 dark:text-gray-400 hover:text-gray-700'
                  }`}
                >
                  <CalendarIcon className="w-3.5 h-3.5" />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                    viewMode === 'list'
                      ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                      : 'text-gray-500 dark:text-gray-400 hover:text-gray-700'
                  }`}
                >
                  <List className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          </div>

          {/* Calendar or List View */}
          {viewMode === 'calendar' ? (
            <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 overflow-hidden">
              {/* Week day headers */}
              <div className="grid grid-cols-7 border-b border-gray-100 dark:border-gray-700">
                {weekDays.map((day) => (
                  <div
                    key={day}
                    className="py-3 text-center text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                  >
                    {day}
                  </div>
                ))}
              </div>

              {/* Calendar grid */}
              <div className="grid grid-cols-7">
                {calendarDays.map((cell, index) => {
                  const dayOccurrences = cell.isCurrentMonth
                    ? occurrencesByDay.get(cell.day) || []
                    : [];
                  const isToday =
                    isCurrentMonth && cell.isCurrentMonth && cell.day === today.getDate();
                  const isSelected = selectedDay === cell.day && cell.isCurrentMonth;
                  const hasItems = dayOccurrences.length > 0;

                  return (
                    <div
                      key={index}
                      onClick={() =>
                        cell.isCurrentMonth && handleSelectDay(isSelected ? null : cell.day)
                      }
                      className={`relative min-h-[100px] lg:min-h-[120px] p-2 border-b border-r border-gray-50 dark:border-gray-700/50 transition-colors cursor-pointer
                          ${!cell.isCurrentMonth ? 'bg-gray-50/50 dark:bg-gray-900/30' : 'hover:bg-gray-50 dark:hover:bg-gray-700/30'}
                          ${isSelected ? 'bg-purple-50 dark:bg-purple-900/20 ring-2 ring-purple-500/30 ring-inset' : ''}
                        `}
                    >
                      {/* Day number */}
                      <div
                        className={`text-sm font-medium mb-1.5 ${
                          !cell.isCurrentMonth
                            ? 'text-gray-300 dark:text-gray-600'
                            : isToday
                              ? 'text-white'
                              : 'text-gray-700 dark:text-gray-300'
                        }`}
                      >
                        {isToday ? (
                          <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-[#16c47f] text-white text-xs font-bold">
                            {cell.day}
                          </span>
                        ) : (
                          cell.day
                        )}
                      </div>

                      {/* Brand logos */}
                      {hasItems && cell.isCurrentMonth && (
                        <div className="flex flex-wrap gap-1">
                          {(expandedDay === cell.day
                            ? dayOccurrences
                            : dayOccurrences.slice(0, 3)
                          ).map((occ, i) => {
                            const brand = getSubscriptionLogo(occ.recurring.description);
                            return (
                              <div key={`${occ.recurring.id}-${i}`} className="group relative">
                                {brand ? (
                                  <div className="w-8 h-8 rounded-full overflow-hidden shadow-sm border-2 border-white dark:border-gray-700 bg-white dark:bg-gray-700 flex items-center justify-center group-hover:scale-110 transition-transform">
                                    <img
                                      src={brand.logoUrl}
                                      alt={brand.name}
                                      className="w-5 h-5 object-contain"
                                      onError={(e) => {
                                        (
                                          e.target as HTMLImageElement
                                        ).parentElement!.style.display = 'none';
                                        (
                                          e.target as HTMLImageElement
                                        ).parentElement!.nextElementSibling?.classList.remove(
                                          'hidden'
                                        );
                                      }}
                                    />
                                  </div>
                                ) : null}
                                <div
                                  className={`${brand ? 'hidden' : ''} w-8 h-8 rounded-full flex items-center justify-center text-white text-[10px] font-bold shadow-sm border-2 border-white dark:border-gray-700 ${
                                    occ.recurring.type === 'income'
                                      ? 'bg-gradient-to-br from-green-400 to-green-600'
                                      : 'bg-gradient-to-br from-[#78909C] to-[#607D8B]'
                                  }`}
                                >
                                  {occ.recurring.description.substring(0, 2).toUpperCase()}
                                </div>

                                {/* Tooltip */}
                                <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 px-2 py-1 bg-gray-900 dark:bg-gray-700 text-white text-[10px] rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-50 shadow-lg">
                                  {occ.recurring.description} · {formatAmount(occ.recurring.amount)}
                                </div>
                              </div>
                            );
                          })}
                          {dayOccurrences.length > 3 && expandedDay !== cell.day && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setExpandedDay(cell.day);
                              }}
                              className="w-8 h-8 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center text-[10px] font-bold text-gray-500 dark:text-gray-400 border-2 border-white dark:border-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors cursor-pointer"
                            >
                              +{dayOccurrences.length - 3}
                            </button>
                          )}
                          {expandedDay === cell.day && dayOccurrences.length > 3 && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setExpandedDay(null);
                              }}
                              className="w-8 h-8 rounded-full bg-gray-200 dark:bg-gray-600 flex items-center justify-center text-[10px] font-bold text-gray-600 dark:text-gray-300 border-2 border-white dark:border-gray-700 hover:bg-gray-300 dark:hover:bg-gray-500 transition-colors cursor-pointer"
                            >
                              −
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              {sortedOccurrences.length === 0 ? (
                <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 p-12 text-center">
                  <CalendarIcon className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
                  <p className="text-gray-500 dark:text-gray-400">{t('calendar.empty' as any)}</p>
                  <button
                    onClick={() => setIsModalOpen(true)}
                    className="mt-4 px-4 py-2 bg-[#16c47f] hover:bg-[#14b371] text-white rounded-xl text-sm font-medium transition-all hover:scale-105"
                  >
                    <Plus className="w-4 h-4 inline mr-1" />
                    {t('calendar.addFirst' as any)}
                  </button>
                </div>
              ) : (
                sortedOccurrences.map((occ, i) => {
                  const brand = getSubscriptionLogo(occ.recurring.description);
                  const dateStr = occ.date.toLocaleDateString('default', {
                    weekday: 'short',
                    day: 'numeric',
                    month: 'short',
                  });
                  const isPast = occ.date < today;

                  return (
                    <div
                      key={`${occ.recurring.id}-${i}`}
                      className={`flex items-center gap-4 bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 p-4 hover:shadow-md transition-all ${
                        isPast ? 'opacity-60' : ''
                      }`}
                    >
                      {/* Logo */}
                      <div className="shrink-0">
                        {brand ? (
                          <div className="w-11 h-11 rounded-full overflow-hidden shadow-md border-2 border-white dark:border-gray-700 bg-white dark:bg-gray-700 flex items-center justify-center">
                            <img
                              src={brand.logoUrl}
                              alt={brand.name}
                              className="w-7 h-7 object-contain"
                              onError={(e) => {
                                (e.target as HTMLImageElement).parentElement!.style.display =
                                  'none';
                                (
                                  e.target as HTMLImageElement
                                ).parentElement!.nextElementSibling?.classList.remove('hidden');
                              }}
                            />
                          </div>
                        ) : null}
                        <div
                          className={`${brand ? 'hidden' : ''} w-11 h-11 rounded-full flex items-center justify-center text-white text-sm font-bold shadow-md border-2 border-white dark:border-gray-700 ${
                            occ.recurring.type === 'income'
                              ? 'bg-gradient-to-br from-green-400 to-green-600'
                              : 'bg-gradient-to-br from-[#78909C] to-[#607D8B]'
                          }`}
                        >
                          {occ.recurring.description.substring(0, 2).toUpperCase()}
                        </div>
                      </div>

                      {/* Info */}
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-gray-900 dark:text-white truncate">
                          {brand?.name || occ.recurring.description}
                        </p>
                        <div className="flex items-center gap-2 mt-0.5">
                          <span className="text-xs text-gray-500 dark:text-gray-400 capitalize">
                            {dateStr}
                          </span>
                          <span className="text-gray-300 dark:text-gray-600">·</span>
                          <span className="text-xs text-gray-400 dark:text-gray-500 capitalize">
                            {occ.recurring.frequency}
                          </span>
                        </div>
                      </div>

                      {/* Amount */}
                      <div className="text-right shrink-0">
                        <p
                          className={`font-bold text-base ${
                            occ.recurring.type === 'income' ? 'text-[#16c47f]' : 'text-[#78909C]'
                          }`}
                        >
                          {occ.recurring.type === 'income' ? '+' : '-'}
                          {formatAmount(occ.recurring.amount)}
                        </p>
                        <div className="flex items-center justify-end gap-1 mt-0.5">
                          {occ.recurring.type === 'income' ? (
                            <ArrowUpCircle className="w-3 h-3 text-[#16c47f]" />
                          ) : (
                            <ArrowDownCircle className="w-3 h-3 text-[#78909C]" />
                          )}
                          <span className="text-[10px] text-gray-400">
                            {occ.recurring.type === 'income'
                              ? t('calendar.incomeLabel' as any)
                              : t('calendar.expenseLabel' as any)}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          )}

          {/* Selected Day Detail Panel */}
          {selectedDay && viewMode === 'calendar' && (
            <div ref={detailPanelRef} className="mt-4 overflow-hidden scroll-mt-4">
              <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 p-5">
                <h3 className="font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                  <CalendarIcon className="w-4 h-4 text-purple-500" />
                  {selectedDay} {currentDate.toLocaleString('default', { month: 'long' })}
                </h3>
                {(occurrencesByDay.get(selectedDay) || []).length === 0 ? (
                  <p className="text-sm text-gray-400 dark:text-gray-500">
                    {t('calendar.nothingOnDay' as any)}
                  </p>
                ) : (
                  <div className="space-y-3">
                    {(occurrencesByDay.get(selectedDay) || []).map((occ, i) => {
                      const brand = getSubscriptionLogo(occ.recurring.description);
                      return (
                        <div
                          key={`detail-${occ.recurring.id}-${i}`}
                          className="flex items-center gap-3 p-3 rounded-xl bg-gray-50 dark:bg-gray-700/50 group"
                        >
                          {brand ? (
                            <div className="w-10 h-10 rounded-full overflow-hidden shadow-sm border-2 border-white dark:border-gray-600 bg-white dark:bg-gray-700 flex items-center justify-center">
                              <img
                                src={brand.logoUrl}
                                alt={brand.name}
                                className="w-6 h-6 object-contain"
                              />
                            </div>
                          ) : (
                            <div
                              className={`w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-bold border-2 border-white dark:border-gray-600 ${
                                occ.recurring.type === 'income'
                                  ? 'bg-gradient-to-br from-green-400 to-green-600'
                                  : 'bg-gradient-to-br from-[#78909C] to-[#607D8B]'
                              }`}
                            >
                              {occ.recurring.description.substring(0, 2).toUpperCase()}
                            </div>
                          )}
                          <div className="flex-1">
                            <p className="font-medium text-sm text-gray-900 dark:text-white">
                              {brand?.name || occ.recurring.description}
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-400 capitalize">
                              {occ.recurring.frequency} · {occ.recurring.account?.name || ''}
                            </p>
                          </div>
                          <div className="flex items-center gap-2">
                            <p
                              className={`font-bold ${
                                occ.recurring.type === 'income'
                                  ? 'text-[#16c47f]'
                                  : 'text-[#78909C]'
                              }`}
                            >
                              {occ.recurring.type === 'income' ? '+' : '-'}
                              {formatAmount(occ.recurring.amount)}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Create/Edit Modal */}
        <RecurringTransactionModal
          isOpen={isModalOpen}
          onClose={() => {
            setIsModalOpen(false);
            // Only clear editingRecurring if we're NOT opening the modify dialog
            if (!pendingModifyRef.current) {
              setEditingRecurring(null);
            }
            pendingModifyRef.current = false;
            loadRecurringTransactions();
          }}
          onSave={handleSaveRecurring}
          recurring={editingRecurring}
        />

        {/* Manage Recurrences Modal */}
        <ManageRecurrencesModal
          isOpen={isManageOpen}
          onClose={() => setIsManageOpen(false)}
          recurrences={recurringTransactions}
          onAdd={() => {
            setIsManageOpen(false);
            setEditingRecurring(null);
            setIsModalOpen(true);
          }}
          onEdit={(r) => {
            setIsManageOpen(false);
            setEditingRecurring(r);
            setIsModalOpen(true);
          }}
          onDelete={async (id) => {
            const ok = await confirm({
              description: t('recurring.deleteConfirm' as any),
              variant: 'destructive',
            });
            if (ok) {
              await deleteRecurringTransaction(id);
            }
          }}
          onToggleActive={async (id, isActive) => {
            await toggleActive(id, isActive);
          }}
        />

        {/* Modify Recurrence Dialog */}
        <ModifyRecurrenceDialog
          isOpen={isModifyDialogOpen}
          onClose={() => {
            setIsModifyDialogOpen(false);
            setPendingEditData(null);
          }}
          onConfirm={handleModifyConfirm}
        />
      </main>
    </div>
  );
}

// Helper to advance a date by frequency
function advanceDate(date: Date, frequency: string): Date {
  const next = new Date(date);
  switch (frequency) {
    case 'daily':
      next.setDate(next.getDate() + 1);
      break;
    case 'weekly':
      next.setDate(next.getDate() + 7);
      break;
    case 'monthly':
      next.setMonth(next.getMonth() + 1);
      break;
    case 'yearly':
      next.setFullYear(next.getFullYear() + 1);
      break;
  }
  return next;
}

// Parse a date string as a local date (avoids UTC timezone shift)
function parseLocalDate(dateStr: string): Date {
  // If it's a date-only string like "2026-03-15", parse as local
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
    const [y, m, d] = dateStr.split('-').map(Number);
    return new Date(y, m - 1, d);
  }
  // If it has a time component, extract just the date part to avoid timezone issues
  const datePart = dateStr.substring(0, 10);
  if (/^\d{4}-\d{2}-\d{2}$/.test(datePart)) {
    const [y, m, d] = datePart.split('-').map(Number);
    return new Date(y, m - 1, d);
  }
  return new Date(dateStr);
}
